import React, { useEffect, useState } from 'react';
import AdminAppBody from '../../../../components/admin/AdminAppBody';
import { Link } from 'react-router-dom';
import useApiCallHooks from '../../../../hooks/useApiCallHooks';

const ITEMS_PER_PAGE = 6;

const parseDate = (dateStr) => {
  if (!dateStr) return null;
  return new Date(dateStr); // Safe for YYYY-MM-DD format
};

const getStatus = (sessionDate) => {
  const date = parseDate(sessionDate);
  if (!date || isNaN(date)) return 'unknown';

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (date > today) return 'upcoming';
  if (date < today) return 'completed';
  return 'ongoing';
};

const statusColors = {
  upcoming: 'bg-info text-white',
  ongoing: 'bg-warning text-dark',
  completed: 'bg-success text-white',
};

const Schedule = () => {
  const [responce, loading, error, callAPI, statusCode] = useApiCallHooks();
  const [sessionsData, setSessionsData] = useState([]);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  const filteredSessions = sessionsData.filter((session) => {
    const matchesSearch =
      session.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      session.participant?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesType =
      typeFilter === 'all' || session.type?.toLowerCase() === typeFilter.toLowerCase();

    const sessionStatus = getStatus(session.start_date);
    const matchesStatus = statusFilter === 'all' || sessionStatus === statusFilter;

    return matchesSearch && matchesType && matchesStatus;
  });

  const totalPages = Math.ceil(filteredSessions.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentSessions = filteredSessions.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  useEffect(() => {
    callAPI("get", "mentor/session-list");
  }, []);

  useEffect(() => {
    if (Array.isArray(responce?.data)) {
      setSessionsData(responce.data);
    }
  }, [responce]);

  return (
    <AdminAppBody
      content={
        <div className="min-vh-100 bg-light p-4">
          <div className="mb-4 d-flex justify-content-between align-items-center">
            <h1 className="h4 fw-bold text-dark mb-0">Session List</h1>
            <Link to="/mentor/session/new" className="btn btn-primary">
              <i className="bi bi-plus-lg me-1"></i> +New Session
            </Link>
          </div>

          <div className="row g-3 mb-4">
            {[
              { icon: "bar-chart", label: 'Total Sessions', value: sessionsData.length },
              { icon: "graph-up-arrow", label: 'Upcoming', value: sessionsData.filter(s => getStatus(s.start_date) === 'upcoming').length },
              { icon: "check-circle", label: 'Completed', value: sessionsData.filter(s => getStatus(s.start_date) === 'completed').length },
              { icon: "calendar-event", label: 'Today', value: sessionsData.filter(s => getStatus(s.start_date) === 'ongoing').length },
            ].map((stat, index) => (
              <div className="col-sm-6 col-lg-3" key={index}>
                <div className="card border shadow-sm">
                  <div className="card-body d-flex align-items-center gap-3">
                    <div className="bg-primary-subtle rounded-circle d-flex align-items-center justify-content-center" style={{ width: 48, height: 48 }}>
                      <i className={`bi bi-${stat.icon} text-primary`}></i>
                    </div>
                    <div>
                      <p className="mb-1 text-muted small">{stat.label}</p>
                      <h5 className="mb-0">{stat.value}</h5>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="card border shadow-sm mb-4">
            <div className="card-body row g-3">
              <div className="col-md-4 position-relative">
                <i className="bi bi-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                <input
                  type="text"
                  className="form-control ps-5"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>

              <div className="col-md-4 d-flex align-items-center gap-2">
                <label className="form-label m-0">Filter:</label>
                <select
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">Select Status</option>
                  <option value="upcoming">Upcoming</option>
                  <option value="completed">Completed</option>
                  <option value="ongoing">Ongoing</option>
                </select>
              </div>

              <div className="col-md-4">
                <select
                  className="form-select"
                  value={typeFilter}
                  onChange={(e) => {
                    setTypeFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="all">Select Type</option>
                  <option value="individual">Individual</option>
                  <option value="group">Group</option>
                </select>
              </div>
            </div>
          </div>

          <div className="row g-3">
            {currentSessions.map((session) => {
              const status = getStatus(session.start_date);
              const badgeClass = statusColors[status] || 'bg-secondary text-white';

              return (
                <div className="col-sm-6 col-md-4 col-lg-4" key={session.id}>
                  <div className="card border shadow-sm h-100 position-relative" style={{ minHeight: '240px' }}>
                    <span className={`position-absolute top-0 end-0 mt-2 me-2 px-3 py-1 rounded-pill fw-semibold ${badgeClass}`} style={{ fontSize: '0.75rem', boxShadow: '0 0 6px rgba(0,0,0,0.1)' }}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </span>

                    <div className="card-body d-flex flex-column">
                      <div className="d-flex justify-content-between mb-2">
                        <h6 className="card-title mb-0">{session.title}</h6>
                        <i className="bi bi-three-dots-vertical text-muted"></i>
                      </div>

                      <div className="mb-2 text-muted small" style={{ fontSize: '0.8rem' }}>
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <i className="bi bi-calendar-event"></i> <span>{session.start_date}</span>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <i className="bi bi-clock"></i> <span>{session.time}</span>
                        </div>
                      </div>

                      <div className="mb-2 text-muted small d-flex align-items-center gap-2" style={{ fontSize: '0.8rem' }}>
                        <i className="bi bi-person-badge"></i> <span>{session.type}</span>
                      </div>

                      <p className="mb-3 text-dark small flex-grow-1" style={{ fontSize: '0.85rem' }}>
                        {session.participant || 'No participant'}
                      </p>

                      <div className="text-end">
                        <a href={session.url || '#'} className="btn btn-sm btn-outline-primary rounded d-inline-flex align-items-center gap-1" target="_blank" rel="noopener noreferrer">
                          <i className="bi bi-camera-video"></i>
                          <span>Join Link</span>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <nav className="mt-4">
              <ul className="pagination justify-content-end">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)}>Previous</button>
                </li>
                {[...Array(totalPages)].map((_, index) => (
                  <li key={index} className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}>
                    <button className="page-link" onClick={() => setCurrentPage(index + 1)}>
                      {index + 1}
                    </button>
                  </li>
                ))}
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)}>Next</button>
                </li>
              </ul>
            </nav>
          )}
        </div>
      }
    />
  );
};

export default Schedule;
